import React from "react";
import { WSQueries } from "../../../../query/WSQuery";
import {
  PayeeRowsQuery,
  PayeeRowsQueryFilter,
  PayerPayeeEngagementSearchableStatus
} from "../../../../services/search";
import { useUrlQueryFilters } from "../../../../utils/router";
import { usePayeeRowsSummaryQuery } from "../../../../query/search/payee/queries/usePayeeRowsSummaryQuery";
import {
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { WSInfoBox } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useBackgroundChecksSettingsQuery } from "../../../../query/backgroundChecks/queries/useBackgroundChecksSettingsQuery";
import { useSharedFileSettingsQuery } from "../../../../query/sharedFile/query/useSharedFileSettingsQuery";
import { useUserId } from "../../../../query/hooks/helpers";
import { useMonitorSettingsQuery } from "../../../../query/monitorSettings/query/useMonitorSettingsQuery";
import { getRequirementDefinitionTemplateByParams } from "../../../RequirementDefinitions/utils/getRequirementDefinitionTemplateByParams";
import { selectorPolicyTypesFromSettings } from "../../modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";

export type AddRequirementInformationProps = {
  requirementDefinition?: IRequirementDefinitionResponse;
  engagementId: string;
};

const USER_IDS_WITH_HIDDEN_PRICE = [
  //https://linear.app/wingspan/issue/PLA-855/fe-hide-idv-requirement-pricing-for-wsol-dbhpuhdhh7dute93vcqxvf
  "DbhpUHdhH7Dute93VCqXvF"
];

export const AddRequirementInformation: React.FC<
  AddRequirementInformationProps
> = ({ engagementId, requirementDefinition }) => {
  const userId = useUserId();

  const filter: PayeeRowsQueryFilter = {
    searchableStatus: {
      "!=": PayerPayeeEngagementSearchableStatus.Inactive
    },
    "engagements.engagementId": engagementId
  };
  const { filters } = useUrlQueryFilters<PayeeRowsQuery>({
    filter: filter,
    sort: {
      updatedAt: "desc"
    }
  });

  const queryPayeeRowsSummary = usePayeeRowsSummaryQuery(filters, {
    enabled:
      requirementDefinition?.requirementType &&
      [
        RequirementType.BackgroundCheck,
        RequirementType.SharedFile,
        RequirementType.IdentityVerification
      ].includes(requirementDefinition?.requirementType)
  });

  const queryBackgroundChecksSettings = useBackgroundChecksSettingsQuery(
    requirementDefinition?.dataSourceId!,
    {
      enabled:
        !!requirementDefinition?.dataSourceId &&
        requirementDefinition?.requirementType ===
          RequirementType.BackgroundCheck
    }
  );

  const querySharedFilesSettings = useSharedFileSettingsQuery(
    requirementDefinition?.dataSourceId!,
    {
      enabled:
        !!requirementDefinition?.dataSourceId &&
        requirementDefinition?.requirementType === RequirementType.SharedFile
    }
  );

  const queryMonitorSettings = useMonitorSettingsQuery(
    requirementDefinition?.dataSourceId!,
    {
      enabled:
        !!requirementDefinition?.dataSourceId &&
        requirementDefinition?.requirementType ===
          RequirementType.InsuranceCoverage
    }
  );

  const defaultView: React.ReactNode = (
    <WSInfoBox
      title="Requirement added will impact your existing contractors"
      p="L"
    >
      Your existing contractors will be notified of the requirement added to
      this engagement. The requirement will also apply to any future contractors
      added to this engagement.
    </WSInfoBox>
  );

  const views: Record<RequirementType, React.ReactNode> = {
    [RequirementType.BackgroundCheck]: (
      <WSQueries
        renderErrors={() => defaultView}
        queries={{ queryPayeeRowsSummary, queryBackgroundChecksSettings }}
      >
        {({ queryPayeeRowsSummaryData, queryBackgroundChecksSettingsData }) => {
          return (
            <>
              <WSInfoBox
                title={`Requirement added will impact ${queryPayeeRowsSummaryData.listSize} existing contractors`}
                p="L"
              >
                {queryPayeeRowsSummaryData.listSize} existing contractors will
                be notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              <WSInfoBox title="Background check package" p="L">
                The selected background check package is the Wingspan Standard
                package and will cost{" "}
                {formatMoney(
                  queryBackgroundChecksSettingsData.package?.costPerCheck || 0,
                  "USD"
                )}{" "}
                per check.
              </WSInfoBox>
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.SharedFile]: (
      <WSQueries
        renderErrors={() => defaultView}
        queries={{ queryPayeeRowsSummary, querySharedFilesSettings }}
      >
        {({ queryPayeeRowsSummaryData, querySharedFilesSettingsData }) => {
          return (
            <>
              <WSInfoBox
                title={`Requirement added will impact ${queryPayeeRowsSummaryData.listSize} existing contractors`}
                p="L"
              >
                {queryPayeeRowsSummaryData.listSize} existing contractors will
                be notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              {querySharedFilesSettingsData.cost ? (
                <WSInfoBox title="Document validation requirement" p="L">
                  The selected document validation requirement will cost{" "}
                  {formatMoney(querySharedFilesSettingsData.cost, "USD")} per
                  validation.
                </WSInfoBox>
              ) : null}
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.IdentityVerification]: (
      <WSQueries
        renderErrors={() => defaultView}
        queries={{ queryPayeeRowsSummary }}
      >
        {({ queryPayeeRowsSummaryData }) => {
          const isPriceHidden = USER_IDS_WITH_HIDDEN_PRICE.includes(userId);

          return (
            <>
              <WSInfoBox
                title={`Requirement added will impact ${queryPayeeRowsSummaryData.listSize} existing contractors`}
                p="L"
              >
                {queryPayeeRowsSummaryData.listSize} existing contractors will
                be notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>
              {isPriceHidden ? null : (
                <WSInfoBox title="Document validation requirement" p="L">
                  The selected document validation requirement will cost{" "}
                  {formatMoney(5, "USD")} per validation.
                </WSInfoBox>
              )}
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.Signature]: defaultView,
    [RequirementType.InsuranceCoverage]: (
      <WSQueries
        renderErrors={() => defaultView}
        queries={{ queryPayeeRowsSummary, queryMonitorSettings }}
      >
        {({ queryPayeeRowsSummaryData, queryMonitorSettingsData }) => {
          const template = getRequirementDefinitionTemplateByParams({
            type: RequirementType.InsuranceCoverage,
            policyType: selectorPolicyTypesFromSettings(
              queryMonitorSettingsData
            )
          });

          return (
            <>
              <WSInfoBox
                title={`Requirement added will impact ${queryPayeeRowsSummaryData.listSize} existing contractors`}
                p="L"
              >
                {queryPayeeRowsSummaryData.listSize} existing contractors will
                be notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              {template.costPerSubmission ? (
                <WSInfoBox title="Document validation requirement" p="L">
                  The selected document validation requirement will cost{" "}
                  {formatMoney(template.costPerSubmission, "USD")} per
                  validation.
                </WSInfoBox>
              ) : null}
            </>
          );
        }}
      </WSQueries>
    )
  };

  return (
    <>
      {requirementDefinition?.requirementType
        ? views[requirementDefinition.requirementType]
        : defaultView}
    </>
  );
};
